import {Box, Tooltip} from '@material-ui/core';
import {WarningAmberOutlined} from '@mui/icons-material';
import {IconButton} from '@wandb/weave/components/IconButton';
import {LoadingDots} from '@wandb/weave/components/LoadingDots';
import {Tailwind, TailwindContents} from '@wandb/weave/components/Tailwind';
import _ from 'lodash';
import React, {useCallback, useEffect, useMemo, useRef} from 'react';
import {useAsync} from 'react-use';
import styled from 'styled-components';
import {twMerge} from 'tailwind-merge';

import {
  MOON_100,
  MOON_250,
  MOON_300,
  MOON_800,
} from '../../../../../../../../common/css/color.styles';
import {parseRef, parseRefMaybe} from '../../../../../../../../react';
import {Button} from '../../../../../../../Button';
import {Icon} from '../../../../../../../Icon';
import {CellValue} from '../../../../../Browse2/CellValue';
import {projectIdFromParts} from '../../../../../Browse3/pages/wfReactInterface/tsDataModelHooks';
import {isWeaveRef} from '../../../../filters/common';
import {NotApplicable} from '../../../../NotApplicable';
import {SmallRef} from '../../../../smallRef/SmallRef';
import {isCustomWeaveTypePayload} from '../../../../typeViews/customWeaveType.types';
import {CustomWeaveTypeDispatcher} from '../../../../typeViews/CustomWeaveTypeDispatcher';
import {ValueViewNumber} from '../../../CallPage/ValueViewNumber';
import {CallLink} from '../../../common/Links';
import {useGetTraceServerClientContext} from '../../../wfReactInterface/traceServerClientContext';
import {useCompareEvaluationsState} from '../../compareEvaluationsContext';
import {
  buildCompositeMetricsMap,
  CompositeScoreMetrics,
  CompositeSummaryMetricGroupForKeyPath,
  DERIVED_SCORER_REF_PLACEHOLDER,
  resolvePeerDimension,
} from '../../compositeMetricsUtil';
import {SIGNIFICANT_DIGITS} from '../../ecpConstants';
import {EvaluationComparisonState} from '../../ecpState';
import {MetricDefinition, MetricValueType} from '../../ecpTypes';
import {
  dimensionUnit,
  flattenedDimensionPath,
  getMetricIds,
  metricDefinitionId,
} from '../../ecpUtil';
import {usePeekCall} from '../../hooks';
import {HorizontalBox, VerticalBox} from '../../Layout';
import {
  ComparisonPill,
  SCORER_VARIATION_WARNING_EXPLANATION,
  SCORER_VARIATION_WARNING_TITLE,
} from '../ScorecardSection/ScorecardSection';
import {HEADER_HIEGHT_PX} from './common';
import {
  PivotedRow,
  removePrefix,
  useExampleCompareDataAndPrefetch,
  useFilteredAggregateRows,
  usePartialTableRequest,
} from './exampleCompareSectionUtil';
import {useInputCachingManager} from './useInputCachingManager';

const SIDEBAR_WIDTH_PX = 250;
const MIN_EVAL_WIDTH_PX = 350;
const HEADER_HEIGHT_PX = 38;
const TOP_CELL_PADDING_PX = 4;
const SHOW_INPUT_HEADER = true;

const PropKey = styled.div`
  position: sticky;
  top: ${TOP_CELL_PADDING_PX}px;
  overflow: auto;
  text-align: right;
  scrollbar-width: none;
`;
PropKey.displayName = 'S.PropKey';

const GridCell = styled.div<{
  colSpan?: number;
  rowSpan?: number;
  button?: boolean;
}>`
  font-size: 14px;
  border-top: 0.5px solid ${MOON_250};
  border-bottom: 0.5px solid ${MOON_250};
  border-right: 1px solid ${MOON_250};
  grid-column-end: span ${props => props.colSpan || 1};
  grid-row-end: span ${props => props.rowSpan || 1};
  padding: ${TOP_CELL_PADDING_PX}px 8px;
  background-color: white;
  // Hover should show click mouse icon
  // and slowly highlight blue like a button
  ${props =>
    props.button &&
    `
    cursor: pointer;
    transition: background-color 0.2s;
    &:hover {
      background-color: ${MOON_300};
    }
  `}
`;
GridCell.displayName = 'S.GridCell';

const GridCellSubgrid = styled.div<{
  colSpan?: number;
  rowSpan?: number;
  colsTemp?: string;
  rowsTemp?: string;
}>`
  grid-column-end: span ${props => props.colSpan || 1};
  grid-row-end: span ${props => props.rowSpan || 1};
  padding: 0px;
  background-color: white;
  display: grid;
  grid-template-rows: ${props => props.rowsTemp || 'subgrid'};
  grid-template-columns: ${props => props.colsTemp || 'subgrid'};
  overflow: auto;
`;
GridCellSubgrid.displayName = 'S.GridCellSubgrid';

const GridContainer = styled.div<{colsTemp: string; rowsTemp: string}>`
  display: grid;
  overflow: auto;
  grid-template-columns: ${props => props.colsTemp};
  grid-template-rows: ${props => props.rowsTemp};
`;
GridContainer.displayName = 'S.GridContainer';

const centeredTextStyleMixin: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  textAlign: 'center',
};

const stickyHeaderStyleMixin: React.CSSProperties = {
  ...centeredTextStyleMixin,
  position: 'sticky',
  top: 0,
  zIndex: 1,
  backgroundColor: MOON_100,
  fontWeight: 600,
};

const stickySidebarStyleMixin: React.CSSProperties = {
  position: 'sticky',
  left: 0,
  zIndex: 1,
  backgroundColor: MOON_100,
  fontWeight: 600,
};

const stickySidebarHeaderMixin: React.CSSProperties = {
  ...stickyHeaderStyleMixin,
  ...stickySidebarStyleMixin,
  zIndex: 2,
};

/**
 * This component will occupy the entire space provided by the parent container.
 * It is intended to be used in the CompareEvaluations page, as it depends on
 * the EvaluationComparisonState. However, in principle, it is a general purpose
 * model-output comparison tool. It allows the user to view inputs, then compare
 * model outputs and evaluation metrics across multiple trials.
 *
 * The UX design is quite complex and particular. It uses a series of nested
 * grids to achieve the desired layout, which contains many rules for stickiness
 * and scrolling. Getting this to work correctly was a significant challenge.
 * The basic idea is:
 *    * There are 3 main sections, stacked vertically: Input, Model Outputs, and
 *      Metrics
 *        * Each of these sections has a sticky header
 *    * The left sticky sidebar contains the property keys for each section.
 *        * A special case are the metrics, which are grouped by the scoring
 *          function
 *    * The input section only has one "value" column. It should scroll
 *      vertically, but not horizontally
 *    * The model outputs section has a column for each evaluation/model. These
 *      will scroll vertically and horizontally
 *    * The metrics section has a column for each evaluation (aligned with the
 *      model outputs) and then further subdivides into a column for each trial
 *
 *       * THe nested inner trial grid needs to also scroll horizontally and
 *         vertically with it's own inner nested sticky header and sidebar
 *    * The Input and Metrics section are designed to be as small as possible,
 *      with extra space given to the Model Outputs section
 *       * When there is not enough vertical space, all sections should flex
 *         down equally, sharing the space - resulting in all panels always in
 *         view.
 *
 * Since grid system layouts often are not as easy to iterate, I pulled out all
 * the data lookup logic into their own functions so future devs can just focus
 * on the layout, without mixing in the data logic.
 *
 * As of this writing, there are 2 remaining style/UX challenges for this
 * component: There are 2 remaining style/UX challenges for this component:
 *
 * 1. "Fixed-Width Sidebar" - Due the how the grid system works, we cannot have
 *    a sidebar width that responds to the content. It must be fixed.
 *    * Options: allow the users to drag the sidebar width; use a ref to get the
 *      largest width of the sidebar and set all to that width
 * 2. "Sticky Metric Trial Headers": Due to how the grid system works, I could
 *    not get the metric headers to be sticky. I tried many things, but could
 *    not get it to work, while maintaining the desired layout. Challenge for
 *    future devs.
 *    * Example. Go here and make the screen quite small - notice the 3rd eval's trials don't have sticky headers
 *      https://wandb.ai/shawn/humaneval6/weave/compare-evaluations?evaluationCallIds=%5B%2258c9db2c-c1f8-4643-a79d-7a13c55fbc72%22%2C%228563f89b-07e8-4042-9417-e22b4257bf95%22%2C%2232f3e6bc-5488-4dd4-b9c4-801929f2c541%22%2C%2234c0a20f-657f-407e-bb33-277abbb9997f%22%5D
 */
export const ExampleCompareSectionDetailGuarded: typeof ExampleCompareSectionDetail =
  props => {
    const ctx = useCompareEvaluationsState();
    const {filteredRows} = useFilteredAggregateRows(ctx.state);
    if (_.isEmpty(filteredRows)) {
      return (
        <TailwindContents>
          <div className="m-8">
            These evaluations have no common rows between them, or the filter is
            too restrictive.
          </div>
        </TailwindContents>
      );
    }
    return <ExampleCompareSectionDetail {...props} />;
  };

export const ExampleCompareSectionDetail: React.FC<{
  // Not to future devs: `state` here can be derived from the context.
  // This should be removed in a future PR. There are probably other places
  // that can be cleaned up as well.
  state: EvaluationComparisonState;
  onClose: () => void;
}> = props => {
  const ctx = useCompareEvaluationsState();
  // Prefer the method below (since `state` diverging from the
  // context would certainly be a bug)
  // const {state} = ctx;
  const {
    filteredRows,
    outputColumnKeys,
    leafDims: orderedCallIds,
  } = useFilteredAggregateRows(props.state);
  const {setSelectedInputDigest} = ctx;
  const targetIndex = useMemo(() => {
    const selectedDigest = props.state.selectedInputDigest;
    if (selectedDigest) {
      const found = filteredRows.findIndex(
        row => row.inputDigest === selectedDigest
      );
      if (found !== -1) {
        return found;
      }
    }
    return 0;
  }, [filteredRows, props.state.selectedInputDigest]);

  const target = useMemo(() => {
    return filteredRows[targetIndex];
  }, [filteredRows, targetIndex]);

  const partialTableRequest = usePartialTableRequest(props.state);
  const inputCacheManager = useInputCachingManager(partialTableRequest);

  const {targetRowValue, loading: loadingInputValue} =
    useExampleCompareDataAndPrefetch(
      inputCacheManager,
      filteredRows,
      targetIndex
    );

  const inputColumnKeys = useMemo(() => {
    return Object.keys(targetRowValue ?? {});
  }, [targetRowValue]);

  const [selectedTrials, setSelectedTrials] = React.useState<{
    [evalCallId: string]: number;
  }>({});

  const onScorerClick = usePeekCall(
    props.state.summary.entity,
    props.state.summary.project
  );

  const {ref1, ref2} = useLinkHorizontalScroll();

  const compositeScoreMetrics = useMemo(
    () => buildCompositeMetricsMap(props.state.summary, 'score'),
    [props.state.summary]
  );

  if (target == null) {
    return <div>Filter resulted in 0 rows</div>;
  }

  // This section contains the primary helper variable for laying out the grid
  const metricGroupNames = Object.keys(compositeScoreMetrics).filter(
    k => k !== DERIVED_SCORER_REF_PLACEHOLDER
  );

  const inputRef = target.inputRef;
  const numInputProps = inputColumnKeys?.length ?? 0;
  const numOutputKeys = outputColumnKeys?.length ?? 0;

  const numTrials = orderedCallIds.map(leafId => {
    return target.originalRows.filter(row => row.evaluationCallId === leafId)
      .length;
  });
  const numEvals = numTrials.length;
  // Get derived scores, then filter out any not in the selected metrics
  const derivedScores = Object.values(
    getMetricIds(props.state.summary, 'score', 'derived')
  ).filter(
    score => props.state.selectedMetrics?.[flattenedDimensionPath(score)]
  );

  const numMetricScorers = metricGroupNames.length;
  const numDerivedScores = derivedScores.length;
  const numMetricsPerScorer = [
    ...metricGroupNames.map(groupName => {
      return Object.keys(compositeScoreMetrics[groupName].metrics).length;
    }),
    numDerivedScores,
  ];
  const totalMetrics = _.sum(numMetricsPerScorer);

  // This section contains a bunch of helper functions used to lookup
  // data for the grid layout. Originally all this stuff was inlined
  // in the JSX, but it was a mess and hard to iterate on. This way
  // we can focus on the layout and not the data.
  //
  // A few conventions:
  //   * Pretty much everything operates on indexes of the different dimensions:
  //      `inputProp`: A single input property
  //      `outputProp`: A single output property
  //      `eval`: A single evaluation
  //      `trial`: A single trial (index is within an evaluation)
  //      `scorer`: A single scorer
  //      `metric`: A single metric (index is within a scorer)
  //   * `lookup*` helper functions to get the data for a specific dimension
  //   * `[DIMENSION]MapKey` is used to get the key for a component when mapping.
  //   * `[DIMENSION][PROP]Comp` specific component for a dimension's property
  const BASELINE_EVAL_INDEX = 0;

  const lookupIsDerivedMetric = (scorerIndex: number): boolean => {
    return scorerIndex === numMetricScorers;
  };

  const lookupTrialsForEval = (evalIndex: number): PivotedRow[] => {
    const currEvalCallId = orderedCallIds[evalIndex];
    return target.originalRows.filter(
      row => row.evaluationCallId === currEvalCallId
    );
  };

  const lookupSelectedTrialIndexForEval = (evalIndex: number): number => {
    const currEvalCallId = orderedCallIds[evalIndex];
    return selectedTrials[currEvalCallId] || 0;
  };

  const lookupSelectedTrialForEval = (
    evalIndex: number
  ): PivotedRow | undefined => {
    const trialsForThisEval = lookupTrialsForEval(evalIndex);
    return trialsForThisEval[lookupSelectedTrialIndexForEval(evalIndex)];
  };

  const lookupScoreGroupForScorerIndex = (scorerIndex: number) => {
    return compositeScoreMetrics[metricGroupNames[scorerIndex]];
  };

  const lookupScoreGroupMetricsForScorerIndex = (scorerIndex: number) => {
    return lookupScoreGroupForScorerIndex(scorerIndex).metrics;
  };

  const lookupUniqueScorerRefsForScorerIndex = (
    scorerIndex: number
  ): string[] => {
    return lookupScoreGroupForScorerIndex(scorerIndex).scorerRefs;
  };

  const lookupDimensionsForScorer = (
    scorerIndex: number
  ): MetricDefinition[] => {
    const isDerivedMetric = lookupIsDerivedMetric(scorerIndex);
    const lookupAnyDimensionForMetric = (
      sm: CompositeSummaryMetricGroupForKeyPath
    ) => {
      return Object.values(sm.scorerRefs)[0].metric;
    };

    if (isDerivedMetric) {
      return derivedScores;
    }
    return Object.values(
      lookupScoreGroupMetricsForScorerIndex(scorerIndex)
    ).map(lookupAnyDimensionForMetric);
  };

  const lookupDimension = (
    scorerIndex: number,
    metricIndex: number
  ): MetricDefinition => {
    const dimensionsForThisScorer = lookupDimensionsForScorer(scorerIndex);
    return dimensionsForThisScorer[metricIndex];
  };

  const lookupDimensionId = (
    scorerIndex: number,
    metricIndex: number
  ): string => {
    return metricDefinitionId(lookupDimension(scorerIndex, metricIndex));
  };

  const lookupTargetTrial = (
    evalIndex: number,
    trialIndex: number
  ): PivotedRow => {
    const trialsForThisEval = lookupTrialsForEval(evalIndex);
    return trialsForThisEval[trialIndex];
  };

  const lookupMetricValue = (
    evalIndex: number,
    trialIndex: number,
    scorerIndex: number,
    metricIndex: number
  ): MetricValueType | undefined => {
    const targetTrial = lookupTargetTrial(evalIndex, trialIndex);
    const currEvalCallId = orderedCallIds[evalIndex];
    const dimension = lookupDimension(scorerIndex, metricIndex);
    return lookupMetricValueDirect(
      targetTrial.scores,
      currEvalCallId,
      dimension,
      compositeScoreMetrics
    );
  };

  const lookupAggScorerMetricValue = (
    evalIndex: number,
    scorerIndex: number,
    metricIndex: number
  ): MetricValueType | undefined => {
    const currEvalCallId = orderedCallIds[evalIndex];
    const resolvedScoreId = resolvePeerDimension(
      compositeScoreMetrics,
      currEvalCallId,
      lookupDimension(scorerIndex, metricIndex)
    );

    if (resolvedScoreId == null) {
      return undefined;
    }

    return target.scores[metricDefinitionId(resolvedScoreId)][currEvalCallId];
  };

  // End helpers, start layout

  const inputPropMapKey = (inputPropIndex: number) => {
    return inputColumnKeys[inputPropIndex];
  };

  const inputPropKeyComp = (inputPropIndex: number) => {
    return (
      <PropKey
        style={{
          top:
            TOP_CELL_PADDING_PX +
            (SHOW_INPUT_HEADER ? 1 + HEADER_HEIGHT_PX : 0),
        }}>
        {removePrefix(inputColumnKeys[inputPropIndex], 'input.')}
      </PropKey>
    );
  };

  const inputPropValComp = (inputPropIndex: number) => {
    return (
      <ICValueView
        value={targetRowValue?.[inputColumnKeys[inputPropIndex]]}
        autoCollapseLen={100}
      />
    );
  };

  const evalMapKey = (evalIndex: number) => {
    return orderedCallIds[evalIndex];
  };

  const evalSelectedTrialPredictCallComp = (evalIndex: number) => {
    const currEvalCallId = orderedCallIds[evalIndex];
    const selectedTrial = lookupSelectedTrialForEval(evalIndex);
    if (selectedTrial == null) {
      return null;
    }
    const trialPredict = selectedTrial.predictAndScore._rawPredictTraceData;
    const [trialEntity, trialProject] =
      trialPredict?.project_id.split('/') ?? [];
    const trialOpName = parseRefMaybe(
      trialPredict?.op_name ?? ''
    )?.artifactName;
    const trialCallId = trialPredict?.id;
    const evaluationCall = props.state.summary.evaluationCalls[currEvalCallId];
    if (trialEntity && trialProject && trialOpName && trialCallId) {
      return (
        <Box
          style={{
            overflow: 'hidden',
          }}>
          <CallLink
            entityName={trialEntity}
            projectName={trialProject}
            opName={trialOpName}
            callId={trialCallId}
            icon={<Icon name="filled-circle" color={evaluationCall.color} />}
            color={MOON_800}
          />
        </Box>
      );
    }
    return null;
  };

  const evalTrialSelectComp = (evalIndex: number, trialIndex: number) => {
    const currEvalCallId = orderedCallIds[evalIndex];
    const selectedTrialNdx = lookupSelectedTrialIndexForEval(evalIndex);
    return (
      <Button
        size="small"
        variant={selectedTrialNdx === trialIndex ? 'primary' : 'secondary'}
        onClick={() => {
          setSelectedTrials(curr => {
            return {
              ...curr,
              [currEvalCallId]: trialIndex,
            };
          });
        }}
        icon="show-visible">
        {trialIndex.toString()}
      </Button>
    );
  };

  const evalAggScorerMetricComp = (
    evalIndex: number,
    scorerIndex: number,
    metricIndex: number
  ) => {
    const dimension = lookupDimension(scorerIndex, metricIndex);
    const summaryValue = lookupAggScorerMetricValue(
      evalIndex,
      scorerIndex,
      metricIndex
    );
    const baselineValue = lookupAggScorerMetricValue(
      BASELINE_EVAL_INDEX,
      scorerIndex,
      metricIndex
    );
    return evalAggScorerMetricCompGeneric(
      dimension,
      summaryValue,
      baselineValue
    );
  };

  const evalTrialScorerMetricValueComp = (
    evalIndex: number,
    trialIndex: number,
    scorerIndex: number,
    metricIndex: number
  ) => {
    const metricValue = lookupMetricValue(
      evalIndex,
      trialIndex,
      scorerIndex,
      metricIndex
    );
    if (metricValue == null) {
      return <NotApplicable />;
    }

    return <CellValue value={metricValue} />;
  };

  const evalTrialScorerMetricOnClick = (
    evalIndex: number,
    trialIndex: number,
    scorerIndex: number,
    metricIndex: number
  ) => {
    const scoreId = lookupDimensionId(scorerIndex, metricIndex);
    const targetTrial = lookupTargetTrial(evalIndex, trialIndex);

    if (lookupIsDerivedMetric(scorerIndex)) {
      return undefined;
    }
    return () =>
      onScorerClick(
        targetTrial.predictAndScore.scoreMetrics[scoreId].sourceCallId
      );
  };

  const scorerComp = (scorerIndex: number) => {
    if (lookupIsDerivedMetric(scorerIndex)) {
      return null;
    }
    const scorerRefs = lookupUniqueScorerRefsForScorerIndex(scorerIndex);

    let inner: JSX.Element | null = null;
    if (scorerRefs.length === 0) {
      inner = null;
    } else if (scorerRefs.length === 1) {
      const parsedRef = parseRef(scorerRefs[0]);
      inner = <SmallRef objRef={parsedRef} iconOnly />;
    } else {
      inner = (
        <Tooltip
          title={
            SCORER_VARIATION_WARNING_TITLE +
            ': ' +
            SCORER_VARIATION_WARNING_EXPLANATION
          }>
          <WarningAmberOutlined color="warning" />
        </Tooltip>
      );
    }

    return (
      <VerticalBox
        style={{
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          height: '100%',
          paddingLeft: '2px',
        }}>
        {inner}
      </VerticalBox>
    );
  };

  const scorerMetricKeyComp = (scorerIndex: number, metricIndex: number) => {
    const dimensionsForThisScorer = lookupDimensionsForScorer(scorerIndex);
    return (
      <PropKey>
        {flattenedDimensionPath(dimensionsForThisScorer[metricIndex])}
      </PropKey>
    );
  };

  const header = (
    <HorizontalBox
      sx={{
        justifyContent: 'space-between',
        alignItems: 'center',
        bgcolor: MOON_100,
        padding: '16px',
        height: HEADER_HIEGHT_PX,
        borderBottom: `1px solid ${MOON_250}`,
      }}>
      <HorizontalBox
        sx={{
          alignItems: 'center',
          flex: 1,
          gridGap: '8px',
        }}>
        <Tooltip title="Previous example">
          <IconButton
            disabled={targetIndex === 0}
            onClick={() => {
              setSelectedInputDigest(filteredRows[targetIndex - 1].inputDigest);
            }}>
            <Icon name="chevron-up" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Next example">
          <IconButton
            disabled={targetIndex === filteredRows.length - 1}
            onClick={() => {
              setSelectedInputDigest(filteredRows[targetIndex + 1].inputDigest);
            }}>
            <Icon name="chevron-down" />
          </IconButton>
        </Tooltip>
        <Box
          style={{
            flex: 0,
            marginLeft: '8px',
            marginRight: '4px',
          }}>
          {inputRef && <SmallRef objRef={inputRef} iconOnly />}
        </Box>
        <Box
          style={{
            fontWeight: '600',
            flex: 1,
          }}>
          {`Example ${targetIndex + 1} of ${filteredRows.length}`}
        </Box>
      </HorizontalBox>

      <HorizontalBox sx={{gridGap: '8px'}}>
        <Tooltip title="Close">
          <IconButton
            onClick={() => {
              props.onClose();
            }}>
            <Icon name={'close'} />
          </IconButton>
        </Tooltip>
      </HorizontalBox>
    </HorizontalBox>
  );

  return (
    // The outermost container
    <VerticalBox
      sx={{
        height: '100%',
        width: '100%',
        gridGap: '0px',
      }}>
      {/* Insert the header */}
      {header}
      {/* Setup the outermost grid. Notice the rowsTemp - this is key to having
      the input and metric sections flex down when there is not enough space, and
      the model outputs get the extra space.
       */}
      <GridContainer
        colsTemp={'auto'}
        rowsTemp={`fit-content(100%) auto fit-content(100%)`}
        style={{
          height: '100%',
          flex: 1,
        }}>
        {/* INPUT SECTION */}
        <GridCellSubgrid
          rowSpan={1}
          colSpan={1}
          rowsTemp={`repeat(${
            numInputProps + (SHOW_INPUT_HEADER ? 1 : 0)
          }, auto)`}
          colsTemp={`${SIDEBAR_WIDTH_PX}px auto`}>
          {/* INPUT HEADER */}
          {SHOW_INPUT_HEADER && (
            <React.Fragment>
              <GridCell style={{...stickySidebarHeaderMixin}}>Input</GridCell>
              <GridCell style={{...stickyHeaderStyleMixin}}>Value</GridCell>
            </React.Fragment>
          )}
          {/* INPUT ROWS */}
          {loadingInputValue || targetRowValue === undefined ? (
            <React.Fragment key={'loading'}>
              <GridCell style={{...stickySidebarStyleMixin}}>
                <LoadingDots />
              </GridCell>
              <GridCell>
                <LoadingDots />
              </GridCell>
            </React.Fragment>
          ) : (
            <>
              {_.range(numInputProps).map(inputPropIndex => {
                return (
                  <React.Fragment key={inputPropMapKey(inputPropIndex)}>
                    <GridCell style={{...stickySidebarStyleMixin}}>
                      {inputPropKeyComp(inputPropIndex)}
                    </GridCell>
                    <GridCell>{inputPropValComp(inputPropIndex)}</GridCell>
                  </React.Fragment>
                );
              })}
            </>
          )}
        </GridCellSubgrid>
        {/* OUTPUT SECTION */}
        <GridCellSubgrid
          ref={ref1}
          rowSpan={1}
          colSpan={1}
          rowsTemp={`${HEADER_HEIGHT_PX}px repeat(${numOutputKeys}, auto)`}
          colsTemp={`${SIDEBAR_WIDTH_PX}px repeat(${numEvals}, minmax(${MIN_EVAL_WIDTH_PX}px, 1fr))`}
          style={{
            scrollbarWidth: 'none',
          }}>
          {/* OUTPUT HEADER */}
          <React.Fragment>
            <GridCell style={{...stickySidebarHeaderMixin}}>
              Model Outputs
            </GridCell>
            {_.range(numEvals).map(evalIndex => {
              return (
                <GridCell
                  key={evalMapKey(evalIndex)}
                  style={{...stickyHeaderStyleMixin}}>
                  {evalSelectedTrialPredictCallComp(evalIndex)}
                </GridCell>
              );
            })}
          </React.Fragment>
          {/* OUTPUT ROWS - Dynamically discovered */}
          <DynamicOutputRows
            orderedCallIds={orderedCallIds}
            selectedTrials={selectedTrials}
            targetOriginalRows={target.originalRows}
            evalMapKey={evalMapKey}
          />
        </GridCellSubgrid>
        {/* METRIC SECTION */}
        <GridCellSubgrid
          ref={ref2}
          rowSpan={1}
          colSpan={1}
          rowsTemp={`${HEADER_HEIGHT_PX}px repeat(${totalMetrics}, auto)`}
          colsTemp={`${SIDEBAR_WIDTH_PX}px repeat(${numEvals}, minmax(${MIN_EVAL_WIDTH_PX}px, 1fr))`}>
          {/* METRIC HEADER */}
          <React.Fragment>
            <GridCell
              style={{
                // in a perfect world, this would be STICKY_SIDEBAR_HEADER,
                // but I can't get the eval metric headers to be sticky. I
                // have spent many hours trying to pull this one off and need
                // to move on. The result is that the metrics headers (and trial selection
                // buttons) will scroll off the screen. Not horrible, but a defeat.
                ...stickySidebarStyleMixin,
                ...centeredTextStyleMixin,
                zIndex: 3,
              }}>
              Metrics
            </GridCell>
            {/* METRIC VALUES */}
            {_.range(numEvals).map(evalIndex => {
              const TRIALS_FOR_EVAL = numTrials[evalIndex];

              // EVAL METRIC SUBGRID
              return (
                <GridCellSubgrid
                  key={evalMapKey(evalIndex)}
                  rowSpan={totalMetrics + 1}
                  colSpan={1}
                  colsTemp={`min-content repeat(${TRIALS_FOR_EVAL} , auto)`}>
                  {/* TRIALS HEADER */}
                  <GridCell style={{...stickySidebarHeaderMixin}}>
                    Trials
                  </GridCell>
                  {_.range(TRIALS_FOR_EVAL).map(trialIndex => {
                    return (
                      <GridCell
                        key={trialIndex}
                        style={{...stickyHeaderStyleMixin}}>
                        {evalTrialSelectComp(evalIndex, trialIndex)}
                      </GridCell>
                    );
                  })}

                  {/* TRIALS VALUES */}
                  {numMetricsPerScorer.map((numMetrics, scorerIndex) => {
                    return _.range(numMetrics).map(metricIndex => {
                      return (
                        <React.Fragment
                          key={
                            scorerIndex.toString() +
                            '.' +
                            metricIndex.toString()
                          }>
                          <GridCell style={{...stickySidebarStyleMixin}}>
                            {evalAggScorerMetricComp(
                              evalIndex,
                              scorerIndex,
                              metricIndex
                            )}
                          </GridCell>
                          {_.range(TRIALS_FOR_EVAL).map(trialIndex => {
                            const onClick = evalTrialScorerMetricOnClick(
                              evalIndex,
                              trialIndex,
                              scorerIndex,
                              metricIndex
                            );

                            return (
                              <GridCell
                                key={trialIndex}
                                button={onClick != null}
                                onClick={onClick}>
                                {evalTrialScorerMetricValueComp(
                                  evalIndex,
                                  trialIndex,
                                  scorerIndex,
                                  metricIndex
                                )}
                              </GridCell>
                            );
                          })}
                        </React.Fragment>
                      );
                    });
                  })}
                </GridCellSubgrid>
              );
            })}
          </React.Fragment>

          {/* SCORER / METRIC KEYS */}
          <GridCellSubgrid
            rowSpan={totalMetrics}
            colSpan={1}
            colsTemp={`45px ${SIDEBAR_WIDTH_PX - 45}px`}
            style={{...stickySidebarStyleMixin}}>
            {numMetricsPerScorer.map((NUM_METRICS_FOR_SCORER, scorerIndex) => {
              return (
                <React.Fragment key={scorerIndex}>
                  <GridCell
                    style={{...stickySidebarStyleMixin}}
                    rowSpan={NUM_METRICS_FOR_SCORER}>
                    {scorerComp(scorerIndex)}
                  </GridCell>
                  {_.range(NUM_METRICS_FOR_SCORER).map(metricIndex => {
                    return (
                      <GridCell
                        key={metricIndex}
                        style={{
                          backgroundColor: MOON_100,
                        }}>
                        {scorerMetricKeyComp(scorerIndex, metricIndex)}
                      </GridCell>
                    );
                  })}
                </React.Fragment>
              );
            })}
          </GridCellSubgrid>
        </GridCellSubgrid>
      </GridContainer>
    </VerticalBox>
  );
};

const ICValueView: React.FC<{value: any; autoCollapseLen?: number}> = ({
  value,
  autoCollapseLen,
}) => {
  // We should merge this with ValueView.tsx. Unfortunately,
  // the styling preferences and sizing differ enough to make
  // this more challenging than it should be.

  let text = '';
  if (value == null) {
    return <NotApplicable />;
  } else if (typeof value === 'object') {
    if (isCustomWeaveTypePayload(value)) {
      // This is a bit arbitrary sizing. Just forcing 300px for now. It would be
      // more ideal if `CustomWeaveTypeDispatcher` had some sort of dynamic sizing
      // that could be applied here.
      return (
        <div style={{width: '100%', height: '300px', overflow: 'hidden'}}>
          <CustomWeaveTypeDispatcher data={value} />
        </div>
      );
    }
    text = JSON.stringify(value || {}, null, 2);
  } else if (typeof value === 'string' && isWeaveRef(value)) {
    return <SmallRef objRef={parseRef(value)} />;
  } else {
    text = value.toString();
  }
  const shouldCollapse = autoCollapseLen && text.length > autoCollapseLen;

  if (shouldCollapse) {
    return (
      <ICValueCollapsibleView text={text} autoCollapseLen={autoCollapseLen} />
    );
  } else {
    return <ICValueNonCollapsibleView>{text}</ICValueNonCollapsibleView>;
  }
};

const ICValueNonCollapsibleView: React.FC<{
  children: React.ReactNode;
  onClick?: () => void;
  className?: string;
}> = ({children, onClick, className}) => {
  return (
    <Tailwind>
      <pre
        className={twMerge(
          "p-0 m-0 font-['Inconsolata'] cursor-default whitespace-pre-wrap",
          className
        )}
        onClick={onClick}>
        {children}
      </pre>
    </Tailwind>
  );
};

const ICValueCollapsibleView: React.FC<{
  text: string;
  autoCollapseLen: number;
}> = ({text, autoCollapseLen}) => {
  const [isCollapsed, setIsCollapsed] = React.useState(true);

  const displayText = useMemo(() => {
    const trimmedText = trimWhitespace(text);

    return isCollapsed
      ? trimmedText.slice(0, autoCollapseLen) + '...'
      : trimmedText;
  }, [text, autoCollapseLen, isCollapsed]);

  return (
    <ICValueNonCollapsibleView
      onClick={() => setIsCollapsed(!isCollapsed)}
      className={'cursor-pointer'}>
      {displayText}
      {isCollapsed && (
        <span className={'font-semibold mr-4 text-blue-600'}>
          (click to expand)
        </span>
      )}
    </ICValueNonCollapsibleView>
  );
};

const trimWhitespace = (str: string) => {
  // Trim leading and trailing whitespace
  return str.replace(/^\s+|\s+$/g, '');
};

/**
 * Allows 2 divs to scroll horizontally in sync. The user
 * should be able to scroll either div and the other should
 * scroll as well.
 */
const useLinkHorizontalScroll = () => {
  const ref1 = useRef<HTMLDivElement>(null);
  const ref2 = useRef<HTMLDivElement>(null);

  const scroll1Handler = useCallback(() => {
    if (ref1.current && ref2.current) {
      ref2.current.scrollLeft = ref1.current.scrollLeft;
    }
  }, []);

  const scroll2Handler = useCallback(() => {
    if (ref1.current && ref2.current) {
      ref1.current.scrollLeft = ref2.current.scrollLeft;
    }
  }, []);

  useEffect(() => {
    const ref1Current = ref1.current;
    const ref2Current = ref2.current;

    if (ref1Current) {
      ref1Current.addEventListener('scroll', scroll1Handler);
    }

    if (ref2Current) {
      ref2Current.addEventListener('scroll', scroll2Handler);
    }

    return () => {
      if (ref1Current) {
        ref1Current.removeEventListener('scroll', scroll1Handler);
      }
      if (ref2Current) {
        ref2Current.removeEventListener('scroll', scroll2Handler);
      }
    };
  }, [scroll1Handler, scroll2Handler]);

  return {ref1, ref2};
};

const adjustValueForDisplay = (
  value: number | boolean | undefined,
  isBooleanAggregate?: boolean
): number | undefined => {
  if (value === undefined) {
    return undefined;
  }
  if (typeof value === 'boolean') {
    return value ? 100 : 0;
  } else if (isBooleanAggregate) {
    return value * 100;
  } else {
    return value;
  }
};

export const evalAggScorerMetricCompGeneric = (
  dimension: MetricDefinition,
  summaryValue: MetricValueType | undefined,
  baselineValue: MetricValueType | undefined
) => {
  const unit = dimensionUnit(dimension, true);
  const isBinary = dimension.scoreType === 'binary';

  const summaryMetric = adjustValueForDisplay(summaryValue, isBinary);

  const baseline = adjustValueForDisplay(baselineValue, isBinary);

  const lowerIsBetter = dimension.shouldMinimize ?? false;

  if (summaryMetric == null) {
    return <NotApplicable />;
  }

  return (
    <HorizontalBox
      style={{
        alignItems: 'center',
        justifyContent: 'space-between',
      }}>
      <HorizontalBox
        style={{
          alignItems: 'center',
          gap: '1px',
        }}>
        <ValueViewNumber
          fractionDigits={SIGNIFICANT_DIGITS}
          value={summaryMetric}
        />
        {unit}
      </HorizontalBox>
      <ComparisonPill
        value={summaryMetric}
        baseline={baseline}
        metricUnit={unit}
        metricLowerIsBetter={lowerIsBetter}
      />
    </HorizontalBox>
  );
};

export const lookupMetricValueDirect = (
  targetTrialScores: PivotedRow['scores'],
  currEvalCallId: string,
  dimension: MetricDefinition,
  compositeScoreMetrics: CompositeScoreMetrics
): MetricValueType | undefined => {
  const resolvedScoreId = resolvePeerDimension(
    compositeScoreMetrics,
    currEvalCallId,
    dimension
  );
  // If we get a valid resolution through the normal path, use that
  if (resolvedScoreId != null) {
    const metricId = metricDefinitionId(resolvedScoreId);
    if (
      targetTrialScores &&
      metricId in targetTrialScores &&
      currEvalCallId in targetTrialScores[metricId]
    ) {
      return targetTrialScores[metricId][currEvalCallId];
    }
  }

  // Fallback: try direct lookup using the original dimension's metric ID
  // This is needed for imperative evaluations that don't get properly resolved
  const originalMetricId = metricDefinitionId(dimension);
  if (
    targetTrialScores &&
    originalMetricId in targetTrialScores &&
    currEvalCallId in targetTrialScores[originalMetricId]
  ) {
    return targetTrialScores[originalMetricId][currEvalCallId];
  }

  return undefined;
};

// Component that dynamically renders output rows based on discovered output keys
const DynamicOutputRows: React.FC<{
  orderedCallIds: string[];
  selectedTrials: {[evalCallId: string]: number};
  targetOriginalRows: PivotedRow[];
  evalMapKey: (evalIndex: number) => string;
}> = ({orderedCallIds, selectedTrials, targetOriginalRows, evalMapKey}) => {
  // Helper to get selected trial for an eval
  const getSelectedTrial = useCallback(
    (evalIndex: number): PivotedRow | undefined => {
      const evalCallId = orderedCallIds[evalIndex];
      const trialsForThisEval = targetOriginalRows.filter(
        row => row.evaluationCallId === evalCallId
      );
      const selectedTrialIndex = selectedTrials[evalCallId] || 0;
      return trialsForThisEval[selectedTrialIndex];
    },
    [orderedCallIds, selectedTrials, targetOriginalRows]
  );

  // Collect all trial call IDs and their project info
  // We need to be careful here to avoid infinite re-renders
  const [callIdsToLoad, entity, project] = useMemo(() => {
    const callIds: string[] = [];
    let firstEntity: string | null = null;
    let firstProject: string | null = null;

    for (let evalIndex = 0; evalIndex < orderedCallIds.length; evalIndex++) {
      const selectedTrial = getSelectedTrial(evalIndex);
      const trialPredict = selectedTrial?.predictAndScore._rawPredictTraceData;

      if (trialPredict?.id && trialPredict?.project_id) {
        const [entity, project] = trialPredict.project_id.split('/');
        if (entity && project) {
          if (!firstEntity) {
            firstEntity = entity;
            firstProject = project;
          }
          // Only include calls from the same entity/project
          if (entity === firstEntity && project === firstProject) {
            callIds.push(trialPredict.id);
          }
        }
      }
    }

    return [callIds, firstEntity, firstProject];
  }, [orderedCallIds, getSelectedTrial]);

  const getTsClient = useGetTraceServerClientContext();

  const {value: callOutputsById, loading: callsLoading} = useAsync(async () => {
    if (callIdsToLoad.length === 0 || !entity || !project) {
      return new Map<string, any>();
    }
    const res = await getTsClient().callsStreamQuery({
      project_id: projectIdFromParts({entity, project}),
      filter: {call_ids: callIdsToLoad},
      columns: ['id', 'output'],
    });
    const calls = (res.calls ?? []) as any[];
    return new Map<string, any>(
      calls.map(c => [c.id as string, c.output ?? null])
    );
  }, [callIdsToLoad, entity, project, getTsClient]);

  // Discover all unique output keys from the loaded calls.
  // If outputs are single primitive values, render a single row named "output".
  const outputKeys = useMemo(() => {
    if (callsLoading) return [];

    let keysSet = new Set<string>();
    let sawObject = false;
    let sawPrimitive = false;

    Array.from(callOutputsById!.values()).forEach(output => {
      if (output != null && typeof output === 'object') {
        sawObject = true;
        Object.keys(output).forEach(key => {
          keysSet.add(key);
        });
      } else {
        sawPrimitive = true;
      }
    });

    if (!sawObject && sawPrimitive) {
      return ['output'];
    }

    return Array.from(keysSet).sort();
  }, [callOutputsById, callsLoading]);

  if (callsLoading) {
    return (
      <>
        <GridCell style={{...stickySidebarStyleMixin}}>
          <LoadingDots />
        </GridCell>
        {orderedCallIds.map((_, evalIndex) => (
          <GridCell key={evalMapKey(evalIndex)}>
            <LoadingDots />
          </GridCell>
        ))}
      </>
    );
  }

  // Render rows for each discovered output key
  return (
    <>
      {outputKeys.map(outputKey => (
        <React.Fragment key={outputKey}>
          <GridCell style={{...stickySidebarStyleMixin}}>
            <PropKey
              style={{
                top: TOP_CELL_PADDING_PX + 1 + HEADER_HEIGHT_PX,
              }}>
              {removePrefix(outputKey, 'output.')}
            </PropKey>
          </GridCell>
          {orderedCallIds.map((evalCallId, evalIndex) => {
            const selectedTrial = getSelectedTrial(evalIndex);
            const trialCallId =
              selectedTrial?.predictAndScore._rawPredictTraceData?.id;
            const output = trialCallId
              ? callOutputsById!.get(trialCallId)
              : undefined;
            const value =
              outputKey === 'output' ? output : (output as any)?.[outputKey];

            return (
              <GridCell key={evalMapKey(evalIndex)}>
                {value !== undefined ? (
                  <ICValueView value={value} autoCollapseLen={100} />
                ) : (
                  <NotApplicable />
                )}
              </GridCell>
            );
          })}
        </React.Fragment>
      ))}
    </>
  );
};
